import { useBranches } from '../products/branch-prices-api';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Alert, Box, Button, Divider, Paper, Stack, Typography } from '@mui/material';
import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { useMemo, useState } from 'react';
import { formatBoxPieces, splitBoxesPieces } from '@tiles-erp/shared';
import { usePagination } from '@tiles-erp/hooks';
import { PageContainer } from '@tiles-erp/ui';
import type { GodownStockMatrixColumn, GodownStockMatrixRow } from '@tiles-erp/shared-types';
import { DataTable } from '../../components/DataTable';
import { SearchableSelect } from '../../components/SearchableSelect';
import { downloadTableExcel, downloadTablePdf, fetchAllListRows, type ExportColumn } from '../../components/ListExportButtons';
import { useCatalogOptions } from '../catalog/api';
import { useProductSizes } from '../products/api';
import { useGodownStockMatrix } from './api';

const quantityLabel = (row: GodownStockMatrixRow, godownId: string): string => {
  const quantity = row.quantities[godownId] ?? 0;
  if (!quantity) return '';
  return formatBoxPieces(quantity, row.piecesPerBox, row.baseUom === 'PIECE' || row.piecesPerBox === 1);
};

const parts = (row: GodownStockMatrixRow, godownId: string) => {
  const qty = row.quantities[godownId] ?? 0;
  return row.baseUom === 'PIECE' || row.piecesPerBox === 1
    ? { boxes: 0, pieces: Math.round(qty * row.piecesPerBox) }
    : splitBoxesPieces(qty, row.piecesPerBox);
};
const dynamicExportColumns = (columns: GodownStockMatrixColumn[]): ExportColumn<GodownStockMatrixRow>[] => [
  { header: 'Product', value: row => row.productName, width: 210 },
  { header: 'Brand', value: row => row.brandName, width: 100 },
  ...columns.flatMap(column => [
    { header: `${column.godownName} Box`, value: (row: GodownStockMatrixRow) => parts(row, column.godownId).boxes, width: 85 },
    { header: `${column.godownName} Pcs`, value: (row: GodownStockMatrixRow) => parts(row, column.godownId).pieces, width: 85 },
  ]),
];

/** One product per row, with every accessible branch and godown across the columns. */
export function BranchGodownStockReportPage(): JSX.Element {
  const pagination = usePagination({ initialPageSize: 100 });
  const branches = useBranches();
  const [branchId, setBranchId] = useState('');
  const [brandId, setBrandId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [sizeMm, setSizeMm] = useState('');
  const [exporting, setExporting] = useState<'excel' | 'pdf' | null>(null);
  const [exportError, setExportError] = useState('');
  const brands = useCatalogOptions('/brands');
  const categories = useCatalogOptions('/categories', brandId || undefined);
  const sizes = useProductSizes();
  const filters = { branchId: branchId || undefined, brandId: brandId || undefined, categoryId: categoryId || undefined, sizeMm: sizeMm || undefined };
  const report = useGodownStockMatrix(pagination.query, filters, Boolean(branchId));

  const columns = useMemo<Array<ColDef<GodownStockMatrixRow> | ColGroupDef<GodownStockMatrixRow>>>(() => {
    return [
      { field: 'productName', headerName: 'Product', minWidth: 240, pinned: 'left' },
      { field: 'brandName', headerName: 'Brand', minWidth: 150 },
      ...(report.data?.columns ?? []).filter(g => g.branchId === branchId).map<ColGroupDef<GodownStockMatrixRow>>(godown => ({
        headerName: godown.godownName, marryChildren: true,
        children: [
          { headerName: 'Box', width: 95, valueGetter: p => p.data ? parts(p.data, godown.godownId).boxes : 0, cellStyle: { textAlign: 'right' } },
          { headerName: 'Pcs', width: 85, valueGetter: p => p.data ? parts(p.data, godown.godownId).pieces : 0, cellStyle: { textAlign: 'right' } },
        ],
      })),
    ];
  }, [report.data?.columns, branchId]);

  const runExport = async (format: 'excel' | 'pdf'): Promise<void> => {
    setExportError('');
    setExporting(format);
    try {
      const rows = await fetchAllListRows<GodownStockMatrixRow>('/stock/godown-matrix', {
        ...filters,
        search: pagination.query.search,
      });
      if (!rows.length) throw new Error('There is no stock to export for these filters.');
      const exportColumns = dynamicExportColumns(report.data?.columns ?? []);
      const filename = `branch-godown-stock-${new Date().toISOString().slice(0, 10)}`;
      if (format === 'excel') downloadTableExcel(`${filename}.xls`, 'Branch godown stock', exportColumns, rows);
      else downloadTablePdf(`${filename}.pdf`, 'Branch godown stock', exportColumns, rows);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : 'The report could not be exported.');
    } finally {
      setExporting(null);
    }
  };

  return (
    <PageContainer
      title="Branch godown stock report"
      subtitle="Select a branch to see each product with separate Box and Pcs columns for every godown."
      actions={<Stack direction="row" spacing={1}>
        <Button variant="outlined" startIcon={<DownloadIcon />} disabled={!branchId || report.isFetching || Boolean(exporting) || !report.data?.items.length}
          onClick={() => void runExport('excel')}>{exporting === 'excel' ? 'Exporting…' : 'Excel'}</Button>
        <Button variant="outlined" startIcon={<PictureAsPdfIcon />} disabled={!branchId || report.isFetching || Boolean(exporting) || !report.data?.items.length}
          onClick={() => void runExport('pdf')}>{exporting === 'pdf' ? 'Exporting…' : 'PDF'}</Button>
      </Stack>}
    >
      <Stack spacing={1.5}>
        {exportError && <Alert severity="error" onClose={() => setExportError('')}>{exportError}</Alert>}
        {report.error && <Alert severity="error">{report.error.message}</Alert>}
        <Paper variant="outlined" sx={{ p: { xs: 1.25, sm: 1.5 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <SearchableSelect label="Branch" value={branchId} allLabel="Select branch" options={(branches.data ?? []).map(b => ({ value: b.id, label: b.name }))} onChange={value => { setBranchId(value); pagination.setPage(1); }} width={{ xs: '100%', sm: 260 }} />
            <SearchableSelect label="Brand" value={brandId} allLabel="All brands"
              options={(brands.data ?? []).map((brand) => ({ value: brand.id, label: brand.name }))}
              onChange={(nextBrandId) => {
                setBrandId(nextBrandId);
                setCategoryId('');
                pagination.setPage(1);
              }} width={{ xs: '100%', sm: 220 }} />
            <SearchableSelect label="Category" value={categoryId} allLabel="All categories"
              options={(categories.data ?? []).map((category) => ({ value: category.id, label: category.name }))}
              onChange={(nextCategoryId) => { setCategoryId(nextCategoryId); pagination.setPage(1); }}
              width={{ xs: '100%', sm: 240 }} />
            <SearchableSelect label="Size" value={sizeMm} allLabel="All sizes"
              options={(sizes.data ?? []).map((size) => ({ value: size, label: size }))}
              onChange={(nextSize) => { setSizeMm(nextSize); pagination.setPage(1); }}
              width={{ xs: '100%', sm: 180 }} />
          </Stack>
        </Paper>
        <Typography variant="body2" color="text.secondary">
          {report.data ? `${report.data.meta.totalItems.toLocaleString('en-IN')} products · ${report.data.columns.length} godowns` : branchId ? 'Loading stock…' : 'Select a branch to view stock.'}
        </Typography>
        <DataTable rows={branchId && report.data?.columns.every(c => c.branchId === branchId) ? report.data.items : []} columns={columns} meta={report.data?.meta}
          pagination={pagination} loading={report.isLoading || report.isFetching}
          searchPlaceholder="Search by SKU or product…" exportable={false} height={650}
          gridOptions={{ groupHeaderHeight: 34 }}
          mobileRowRenderer={(row) => {
            const stocked = (report.data?.columns ?? [])
              .map((column) => ({ column, label: quantityLabel(row, column.godownId) }))
              .filter((entry) => entry.label);
            return (
              <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                <Box sx={{ p: 1.5 }}>
                  <Typography fontWeight={700}>{row.sku} · {row.productName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {[row.brandName, row.categoryName, row.sizeMm].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
                <Divider />
                {stocked.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
                    No stock in accessible godowns
                  </Typography>
                ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto' }}>
                    {stocked.map(({ column, label }) => (
                      <Box key={column.godownId} sx={{ display: 'contents' }}>
                        <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {column.branchName}
                          </Typography>
                          <Typography variant="body2">{column.godownName}</Typography>
                        </Box>
                        <Typography sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider', fontWeight: 700, textAlign: 'right' }}>
                          {label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            );
          }} />
      </Stack>
    </PageContainer>
  );
}
